import { config } from '../config';

// Build a query string from defined, non-empty values (e.g. { marketplace_id, dirPath }).
const qs = (params = {}) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') sp.append(k, v); });
    const s = sp.toString();
    return s ? `?${s}` : '';
};

const authHeaders = (withJson = false) => ({
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
    'Authorization': localStorage.getItem('idToken')
});

// Parse the body and throw on non-2xx so callers see real failures (instead of a false "success"
// that navigates away leaving the list unchanged).
const handle = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.message || data.error?.message || `Request failed (${response.status})`);
    }
    return data;
};

export const v2ConnectorsService = {
    list: async (filters = {}) => {
        const queryParams = new URLSearchParams();
        if (filters.v2_connector_type_id) queryParams.append('v2_connector_type_id', filters.v2_connector_type_id);
        if (filters.marketplace_id) queryParams.append('marketplace_id', filters.marketplace_id);
        if (filters.name) queryParams.append('name', filters.name);
        if (filters.limit) queryParams.append('limit', filters.limit);

        const queryString = queryParams.toString();
        const url = `${config.apiBaseUrl}/v2-connectors${queryString ? `?${queryString}` : ''}`;

        return handle(await fetch(url, { headers: authHeaders() }));
    },

    get: async (id) => {
        return handle(await fetch(`${config.apiBaseUrl}/v2-connectors/${id}`, { headers: authHeaders() }));
    },

    create: async (data) => {
        return handle(await fetch(`${config.apiBaseUrl}/v2-connectors`, {
            method: 'POST',
            headers: authHeaders(true),
            body: JSON.stringify(data)
        }));
    },

    update: async (id, data) => {
        return handle(await fetch(`${config.apiBaseUrl}/v2-connectors/${id}`, {
            method: 'PATCH',
            headers: authHeaders(true),
            body: JSON.stringify(data)
        }));
    },

    delete: async (id) => {
        return handle(await fetch(`${config.apiBaseUrl}/v2-connectors/${id}`, {
            method: 'DELETE',
            headers: authHeaders()
        }));
    },

    // Connector test executor (superadmin admin API).
    getTestInfo: async (connectorId) =>
        handle(await fetch(`${config.apiBaseUrl}/admin/connector-test${qs({ connectorId })}`, { headers: authHeaders() })),
    runTest: async (connectorId, payload) =>
        handle(await fetch(`${config.apiBaseUrl}/admin/connector-test`, {
            method: 'POST', headers: authHeaders(true), body: JSON.stringify({ connectorId, ...payload })
        })),

    // Marketplace-shared connector code files. marketplaceId targets the selected (possibly unsaved)
    // marketplace so the tree reflects the current dropdown choice, not just the saved connector.
    files: {
        list: async (connectorId, marketplaceId) =>
            handle(await fetch(`${config.apiBaseUrl}/v2-connectors/${connectorId}/files${qs({ marketplace_id: marketplaceId })}`, { headers: authHeaders() })),
        create: async (connectorId, data, marketplaceId) =>
            handle(await fetch(`${config.apiBaseUrl}/v2-connectors/${connectorId}/files${qs({ marketplace_id: marketplaceId })}`, {
                method: 'POST', headers: authHeaders(true), body: JSON.stringify(data)
            })),
        update: async (connectorId, fileId, data, marketplaceId) =>
            handle(await fetch(`${config.apiBaseUrl}/v2-connectors/${connectorId}/files/${fileId}${qs({ marketplace_id: marketplaceId })}`, {
                method: 'PATCH', headers: authHeaders(true), body: JSON.stringify(data)
            })),
        remove: async (connectorId, fileId, dirPath, marketplaceId) =>
            handle(await fetch(`${config.apiBaseUrl}/v2-connectors/${connectorId}/files/${fileId || 0}${qs({ dirPath, marketplace_id: marketplaceId })}`, {
                method: 'DELETE', headers: authHeaders()
            })),
        removeAll: async (connectorId, marketplaceId) =>
            handle(await fetch(`${config.apiBaseUrl}/v2-connectors/${connectorId}/files/0${qs({ all: '1', marketplace_id: marketplaceId })}`, {
                method: 'DELETE', headers: authHeaders()
            }))
    }
};
